import React, {
  Children,
  ReactNode,
  memo,
  cloneElement,
  ReactElement,
} from 'react'
import { FieldErrors, get, UseFormReturn } from 'react-hook-form'

import type { TFunction } from 'i18next'

import { FormField, FormFieldProps } from './FormField'
import { FormFieldValidation } from './FormFieldValidation'

type ChildrenFn = (props: {
  invalid: boolean
  warning: boolean
  id: string
  name: string
  ref?: any // TODO: proper type
}) => ReactElement

export interface DefaultFormFieldProps
  extends Pick<
    FormFieldProps,
    'label' | 'style' | 'className' | 'labelTooltip'
  > {
  t: TFunction<any>
  name: string
  errors?: FieldErrors<any> | Record<string, any>
  warnings?: Record<string, any>
  register?: UseFormReturn['register']
  children: ReactNode | ChildrenFn
  validation?: boolean
  invalid?: boolean
  translationsPrefix?: string
}

export const DefaultFormField = memo(
  ({
    t,
    name,
    errors,
    warnings,
    register,
    children,
    label,
    validation = true,
    invalid = undefined,
    translationsPrefix = '',
    ...rest
  }: DefaultFormFieldProps) => {
    const error = get(errors || {}, name)
    const warning = get(warnings || {}, name)

    return (
      <FormField
        {...rest}
        {...{
          error:
            validation === true && error ? (
              <FormFieldValidation {...{ t, name, errors }} />
            ) : undefined,
          warning:
            validation === true && warning ? (
              <FormFieldValidation {...{ t, name, warnings }} />
            ) : undefined,
          label:
            label ??
            t(
              `${
                translationsPrefix ? `${translationsPrefix}.` : ''
              }${name}Label` as any,
            ),
        }}>
        {({ id }) => {
          const props = {
            id,
            name,
            // We need to be able to enforce invalid state even if error field does not contain error
            invalid: typeof invalid === 'boolean' ? invalid : !!error,
            warning: !!warning,
            ref: register,
          }

          return typeof children === 'function'
            ? children(props)
            : Children.map(children, (child: any) =>
                cloneElement(Children.only(child), {
                  ...props,
                  placeholder:
                    child.props?.placeholder ??
                    t(
                      `${
                        translationsPrefix ? `${translationsPrefix}.` : ''
                      }${name}Placeholder` as any,
                      '',
                    ),
                }),
              )
        }}
      </FormField>
    )
  },
)
